import { useMemo, useState, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Form } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useAppContext } from '@/Main';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';
import {
  DASHBOARD_PLOT_HEIGHT,
  chartChrome,
  competitorColors,
  escapeHtml,
} from '../../utils/competition-chart';
import { competitorBrandKey } from '../../utils/dashboard-competitors';
import type { ProjectVisibilityModelBreakdown, ProjectVisibilityTopBrand } from '../../types';
import { DashboardEmptyNote } from './DashboardEmptyNote';
import { ChartLegend, useLegendHidden } from './competition/ChartLegend';

const METRIC_OPTIONS = [
  { key: 'shareOfVoice', label: 'سهم از دیده‌شدن' },
  { key: 'visibilityScore', label: 'دیده‌شدن در AI' },
] as const;
type MetricKey = (typeof METRIC_OPTIONS)[number]['key'];

/** Grouped horizontal bars: top competitors' share of voice or AI visibility, split by engine. */
export function TopCompetitorsByEngineCard({
  models,
  competitors,
  plotHeight = DASHBOARD_PLOT_HEIGHT,
  footer,
  hideHeader,
}: {
  models: ProjectVisibilityModelBreakdown[];
  competitors: ProjectVisibilityTopBrand[];
  plotHeight?: number;
  footer?: ReactNode;
  hideHeader?: boolean;
}) {
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };
  const { hidden, toggle } = useLegendHidden();
  const [metric, setMetric] = useState<MetricKey>('shareOfVoice');

  const visibleModels = useMemo(
    () => models.filter((model) => !hidden.has(model.modelName)),
    [models, hidden],
  );

  const option = useMemo(() => {
    if (!competitors.length || !visibleModels.length) return null;
    const chrome = chartChrome();
    const colors = competitorColors();
    // Category y-axis draws bottom-up; reverse so the first competitor sits on top.
    const ordered = [...competitors].reverse();

    const findValue = (model: ProjectVisibilityModelBreakdown, brand: ProjectVisibilityTopBrand) =>
      model.brands.find((row) => competitorBrandKey(row) === competitorBrandKey(brand))?.[
        metric
      ] ?? 0;

    const colorFor = (model: ProjectVisibilityModelBreakdown) =>
      colors[models.indexOf(model) % colors.length];

    return {
      color: visibleModels.map((model) => colorFor(model)),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: chrome.splitLine, opacity: 0.4 } },
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (params: Array<{ dataIndex: number; seriesName: string; value: number; color: string }>) => {
          if (!params.length) return '';
          const brand = ordered[params[0].dataIndex];
          const rows = params
            .map(
              (param) => `
              <div style="margin-top:4px">
                <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${param.color};margin-inline-end:6px"></span>
                ${escapeHtml(param.seriesName)}: <strong>${param.value}%</strong>
              </div>`,
            )
            .join('');
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">${escapeHtml(brand.canonicalName)}</div>${rows}</div>`;
        },
      },
      legend: { show: false },
      grid: { left: 84, right: 16, top: 12, bottom: 32, containLabel: false },
      xAxis: {
        type: 'value',
        min: 0,
        splitNumber: 4,
        splitLine: { lineStyle: { color: chrome.splitLine } },
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          formatter: '{value}%',
        },
      },
      yAxis: {
        type: 'category',
        data: ordered.map((brand) => brand.canonicalName),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          show: true,
          interval: 0,
          hideOverlap: false,
          align: 'left',
          margin: 82,
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          overflow: 'truncate',
          width: 80,
        },
      },
      series: visibleModels.map((model) => ({
        name: model.modelName,
        type: 'bar',
        barMaxWidth: 14,
        barGap: '15%',
        barCategoryGap: '35%',
        itemStyle: { color: colorFor(model), borderRadius: [0, 4, 4, 0] },
        data: ordered.map((brand) => findValue(model, brand)),
      })),
    };
  }, [competitors, visibleModels, models, metric]);

  const colors = competitorColors();

  const metricPicker = (
    <Form.Select
      size="sm"
      className="fs--2"
      style={{ width: '9rem' }}
      aria-label="انتخاب شاخص نمودار"
      value={metric}
      onChange={(event) => setMetric(event.target.value as MetricKey)}
    >
      {METRIC_OPTIONS.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </Form.Select>
  );

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-3"
          title="سهم از دیده‌شدن رقبای برتر به تفکیک مدل"
          endEl={metricPicker}
        />
      )}
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1">
        {hideHeader ? <div className="mb-2">{metricPicker}</div> : null}
        <div className="flex-grow-1 d-flex align-items-center" dir="ltr">
          {option ? (
            <ReactECharts
              key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
              option={option}
              notMerge
              className="w-100"
              style={{ height: plotHeight }}
              opts={{ renderer: 'svg' }}
            />
          ) : (
            <DashboardEmptyNote minHeight={plotHeight}>
              {competitors.length === 0
                ? 'در این بازه رقیبی در پاسخ‌های AI دیده نشده.'
                : 'همه مدل‌ها از نمودار پنهان شده‌اند.'}
            </DashboardEmptyNote>
          )}
        </div>
        {competitors.length > 0 && models.length > 0 ? (
          <ChartLegend
            onToggle={toggle}
            items={models.map((model, index) => ({
              id: model.modelName,
              label: model.modelName,
              color: colors[index % colors.length],
              hidden: hidden.has(model.modelName),
            }))}
          />
        ) : null}
      </Card.Body>
      {footer}
    </Card>
  );
}
